import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAbpPermissions } from '../../../contexts/AbpPermissionsContext';
import type { ManageLicenseListDto } from '../../../types/manageLicenses';
import LicenseDrawerDetailsTab from './LicenseDrawerDetailsTab';
import LicenseDrawerSessionsTab from './LicenseDrawerSessionsTab';
import LicenseDrawerActivityTab from './LicenseDrawerActivityTab';
import LicenseDrawerQuotasTab from './LicenseDrawerQuotasTab';

interface Props {
  license: ManageLicenseListDto | null;
  onClose: () => void;
}

type TabKey = 'details' | 'sessions' | 'activity' | 'quotas';

export default function LicenseEditDrawer({ license, onClose }: Props) {
  const { t } = useTranslation();
  const { hasPermission } = useAbpPermissions();
  const [tab, setTab] = useState<TabKey>('details');

  const canViewSessions = hasPermission('learnleaf.App.ViewSessions');

  // Reset to details tab whenever a different license is opened
  useEffect(() => {
    if (license !== null) setTab('details');
  }, [license?.id]);

  // Close on Escape key
  useEffect(() => {
    if (license === null) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [license, onClose]);

  if (license === null) return null;

  const visibleTabs: TabKey[] = canViewSessions
    ? ['details', 'sessions', 'activity', 'quotas']
    : ['details', 'activity', 'quotas'];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        className="fixed right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-xl border-l border-neutral-200 z-40 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={t('App.Admin.Licenses.LicenseEditDrawer.AriaLabel')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-start justify-between border-b border-neutral-200 px-4 py-3 gap-2">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-semibold text-neutral-900 truncate">
              {license.studentEmail}
            </span>
            <span className="text-xs text-neutral-500 truncate">{license.bookTitle}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-900 text-xl leading-none shrink-0 mt-0.5"
            aria-label={t('App.Admin.Licenses.LicenseEditDrawer.CloseAriaLabel')}
          >
            ×
          </button>
        </header>

        {/* Tab bar */}
        <nav className="flex border-b border-neutral-200 shrink-0">
          {visibleTabs.map((tabKey) => {
            const tabLabel: Record<TabKey, string> = {
              details: t('App.Admin.Licenses.LicenseEditDrawer.TabDetails'),
              sessions: t('App.Admin.Licenses.LicenseEditDrawer.TabSessions'),
              activity: t('App.Admin.Licenses.LicenseEditDrawer.TabActivity'),
              quotas: t('App.Admin.Licenses.LicenseEditDrawer.TabQuotas'),
            };
            return (
              <button
                key={tabKey}
                type="button"
                onClick={() => setTab(tabKey)}
                className={`px-4 py-2 text-sm transition-colors ${
                  tab === tabKey
                    ? 'border-b-2 border-primary-600 text-primary-600 font-medium'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {tabLabel[tabKey]}
              </button>
            );
          })}
        </nav>

        {/* Tab body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'details' && (
            <LicenseDrawerDetailsTab license={license} onClose={onClose} />
          )}
          {tab === 'sessions' && canViewSessions && (
            <LicenseDrawerSessionsTab license={license} onClose={onClose} />
          )}
          {tab === 'activity' && (
            <LicenseDrawerActivityTab license={license} onClose={onClose} />
          )}
          {tab === 'quotas' && (
            <LicenseDrawerQuotasTab license={license} onClose={onClose} />
          )}
        </div>
      </aside>
    </>
  );
}
